<template>
  <div class="container">
    <svg id="morphing-svg" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
      <polygon id="morphing-shape" :points="currentPoints" :fill="currentColor" />
    </svg>
  </div>
</template>

<script>
import { gsap } from "gsap";

export default {
  data: function () {
    return {
      currentPoints: "",
      currentColor: "#00ff88",
      // 定义多个形状的点集用于变形（多边形顶点坐标）
      // 所有坐标都确保在 viewBox (0,0,800,600) 内，并考虑最大 1.3 倍缩放
      // 中心点在 (400, 300)，留出约 150px 的安全边距
      shapes: [
        "400,200 450,250 350,250", // 三角形
        "400,180 470,250 400,320 330,250", // 菱形
        "400,200 470,220 490,280 470,340 330,340 310,280 330,220", // 六边形
        "400,190 450,220 470,280 450,340 400,360 350,340 330,280 350,220", // 七边形
        "400,180 460,200 480,250 460,300 400,320 340,300 320,250 340,200", // 八边形
      ],
      // 定义颜色数组用于颜色过渡
      colors: ["#00ff88", "#0088ff", "#8800ff", "#ff0088", "#ff8800", "#88ff00"],
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initMorphingAnimation();
    });
  },
  methods: {
    initMorphingAnimation() {
      const shapeElement = document.getElementById("morphing-shape");

      if (!shapeElement) return;

      // 设置初始形状和颜色
      this.currentPoints = this.shapes[0];
      this.currentColor = this.colors[0];
      shapeElement.setAttribute("points", this.shapes[0]);
      shapeElement.setAttribute("fill", this.colors[0]);

      // 使用对象来存储当前形状索引，用于手动更新 points
      const state = {
        morphValue: 0, // 0 到 shapes.length 之间的值
      };

      // 计算形状的中心点
      const getShapeCenter = (pointsString) => {
        const points = pointsString.split(" ").filter((p) => p.trim());
        let sumX = 0,
          sumY = 0,
          count = 0;
        points.forEach((point) => {
          const [x, y] = point.split(",").map(Number);
          if (!isNaN(x) && !isNaN(y)) {
            sumX += x;
            sumY += y;
            count++;
          }
        });
        return count > 0 ? { x: sumX / count, y: sumY / count } : { x: 400, y: 300 };
      };

      // 将形状的点相对于中心点进行平移，使中心点对齐到目标位置
      const normalizeShapeToCenter = (pointsString, targetCenterX, targetCenterY) => {
        const points = pointsString.split(" ").filter((p) => p.trim());
        const currentCenter = getShapeCenter(pointsString);
        const offsetX = targetCenterX - currentCenter.x;
        const offsetY = targetCenterY - currentCenter.y;

        return points
          .map((point) => {
            const [x, y] = point.split(",").map(Number);
            if (!isNaN(x) && !isNaN(y)) {
              return `${x + offsetX},${y + offsetY}`;
            }
            return point;
          })
          .join(" ");
      };

      // 函数：根据 morphValue 计算当前的 points
      const updatePointsFromValue = () => {
        const totalShapes = this.shapes.length;
        const fixedCenterX = 400;
        const fixedCenterY = 300;

        // 将 morphValue 映射到形状索引范围 (0 到 totalShapes-1)
        let normalizedValue = state.morphValue % totalShapes;
        if (normalizedValue < 0) normalizedValue += totalShapes;

        const currentIdx = Math.floor(normalizedValue);
        const nextIdx = currentIdx < totalShapes - 1 ? currentIdx + 1 : 0;
        const progress = normalizedValue - currentIdx;

        // 将两个形状都对齐到同一个中心点
        const currentShapeNormalized = normalizeShapeToCenter(
          this.shapes[currentIdx],
          fixedCenterX,
          fixedCenterY
        );
        const nextShapeNormalized = normalizeShapeToCenter(
          this.shapes[nextIdx],
          fixedCenterX,
          fixedCenterY
        );

        const currentPoints = currentShapeNormalized.split(" ");
        const nextPoints = nextShapeNormalized.split(" ");

        // 计算最大点数，用于处理不同顶点数的形状
        const maxPoints = Math.max(currentPoints.length, nextPoints.length);
        const interpolated = [];

        for (let i = 0; i < maxPoints; i++) {
          let point;
          if (i < currentPoints.length && i < nextPoints.length) {
            // 两个形状都有这个点，进行插值
            const [cx, cy] = currentPoints[i].split(",").map(Number);
            const [nx, ny] = nextPoints[i].split(",").map(Number);
            if (!isNaN(cx) && !isNaN(cy) && !isNaN(nx) && !isNaN(ny)) {
              const x = cx + (nx - cx) * progress;
              const y = cy + (ny - cy) * progress;
              point = `${x},${y}`;
            } else {
              point = i < currentPoints.length ? currentPoints[i] : nextPoints[i];
            }
          } else if (i < currentPoints.length) {
            // 只在当前形状中有这个点，逐渐消失（向中心收缩）
            const [cx, cy] = currentPoints[i].split(",").map(Number);
            if (!isNaN(cx) && !isNaN(cy)) {
              const x = cx + (fixedCenterX - cx) * progress;
              const y = cy + (fixedCenterY - cy) * progress;
              point = `${x},${y}`;
            } else {
              point = currentPoints[i];
            }
          } else {
            // 只在下一个形状中有这个点，逐渐出现（从中心扩展）
            const [nx, ny] = nextPoints[i].split(",").map(Number);
            if (!isNaN(nx) && !isNaN(ny)) {
              const x = fixedCenterX + (nx - fixedCenterX) * progress;
              const y = fixedCenterY + (ny - fixedCenterY) * progress;
              point = `${x},${y}`;
            } else {
              point = nextPoints[i];
            }
          }
          interpolated.push(point);
        }

        const pointsString = interpolated.join(" ");
        shapeElement.setAttribute("points", pointsString);
        this.currentPoints = pointsString;
      };

      // 初始化：立即更新一次以显示第一个形状
      updatePointsFromValue();

      // 创建形状变形动画时间线 - 使用连续动画
      gsap.to(state, {
        morphValue: this.shapes.length,
        duration: this.shapes.length * 3, // 每个形状 3 秒
        ease: "none",
        repeat: -1,
        onUpdate: updatePointsFromValue,
      });

      // 创建颜色变化动画时间线
      const colorTimeline = gsap.timeline({ repeat: -1 });

      this.colors.forEach((color, index) => {
        if (index === 0) {
          colorTimeline.set(shapeElement, { attr: { fill: color } }, 0);
          this.currentColor = color;
        } else {
          colorTimeline.to(shapeElement, {
            duration: 2,
            attr: { fill: color },
            ease: "power1.inOut",
            onUpdate: () => {
              this.currentColor = shapeElement.getAttribute("fill");
            },
          });
        }
      });

      // 返回到第一个颜色
      colorTimeline.to(shapeElement, {
        duration: 2,
        attr: { fill: this.colors[0] },
        ease: "power1.inOut",
        onUpdate: () => {
          this.currentColor = shapeElement.getAttribute("fill");
        },
      });

      // 添加额外的动画效果：旋转和缩放
      gsap.to(shapeElement, {
        rotation: 360,
        transformOrigin: "400 300",
        duration: 20,
        repeat: -1,
        ease: "none",
      });

      gsap.to(shapeElement, {
        scale: 1.2,
        transformOrigin: "400 300",
        duration: 4,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
    },
  },
};
</script>

<style scoped>
.container {
  position: relative;
  background: transparent;
  overflow: hidden;
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(255, 255, 255);
}

#morphing-svg {
  width: 100%;
  height: 100%;
  max-width: 800px;
  max-height: 600px;
  overflow: hidden;
}

#morphing-shape {
  filter: drop-shadow(0 0 10px currentColor);
  transition: filter 0.3s ease;
}
</style>
